// src/components/FAANGWatch.js
import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    CardHeader,
    Grid,
    Chip,
    TextField,
    Checkbox,
    FormControl,
    FormGroup,
    FormControlLabel,
    CircularProgress,
    AppBar,
    Toolbar,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    Paper,
    Avatar,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import WorkIcon from '@mui/icons-material/Work';
import FilterListIcon from '@mui/icons-material/FilterList';
import TuneIcon from '@mui/icons-material/Tune';

// Company logos and colors
const COMPANY_CONFIGS = {
    'Amazon': {
        logo: 'https://upload.wikimedia.org/wikipedia/commons/4/4a/Amazon_icon.svg',
        color: '#FF9900'
    },
    'Apple': {
        logo: 'https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg',
        color: '#555555'
    },
    'Google': {
        logo: 'https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg',
        color: '#4285F4'
    },
    'Microsoft': {
        logo: 'https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg',
        color: '#00A4EF'
    },
    'Meta': {
        logo: 'https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg',
        color: '#0668E1'
    },
    'Netflix': {
        logo: 'https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg',
        color: '#E50914'
    },
    'Nvidia': {
        logo: 'https://upload.wikimedia.org/wikipedia/commons/2/21/Nvidia_logo.svg',
        color: '#76B900'
    },
    'Snapchat': {
        logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/c/c4/Snapchat_logo.svg/480px-Snapchat_logo.svg.png',
        color: '#FFFC00'
    },
    'Uber': {
        logo: 'https://upload.wikimedia.org/wikipedia/commons/5/58/Uber_logo_2018.svg',
        color: '#000000'
    }
};

const JobCard = ({ job, onClick }) => {
    const companyConfig = COMPANY_CONFIGS[job.company] || {};

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                }
            }}
            onClick={() => onClick(job)}
        >
            <CardHeader
                avatar={
                    <Avatar
                        src={companyConfig.logo}
                        alt={job.company}
                        sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: companyConfig.color || '#fff',
                            padding: '2px'
                        }}
                    />
                }
                title={job.company}
                subheader={job.locations[0]}
            />
            <CardContent>
                <Typography variant="h6" component="div" gutterBottom noWrap>
                    {job.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {job.categories && job.categories.map((category, index) => (
                        <Chip
                            key={index}
                            label={category}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    ))}
                </Box>
                {job.min_years_of_experience && (
                    <Typography variant="body2" color="text.secondary">
                        Experience: {job.min_years_of_experience}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

const FilterDrawer = ({
    open,
    onClose,
    companies,
    selectedCompanies,
    setSelectedCompanies,
    categories,
    selectedCategories,
    setSelectedCategories,
    locations,
    selectedLocations,
    setSelectedLocations
}) => {
    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: 320 }
            }}
        >
            <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Filters</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Typography variant="subtitle1" sx={{ mb: 1 }}>Companies</Typography>
                <FormGroup>
                    {companies.map((company) => (
                        <FormControlLabel
                            key={company}
                            control={
                                <Checkbox
                                    checked={selectedCompanies.includes(company)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedCompanies([...selectedCompanies, company]);
                                        } else {
                                            setSelectedCompanies(selectedCompanies.filter(c => c !== company));
                                        }
                                    }}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar
                                        src={COMPANY_CONFIGS[company]?.logo}
                                        alt={company}
                                        sx={{ width: 24, height: 24 }}
                                    />
                                    {company}
                                </Box>
                            }
                        />
                    ))}
                </FormGroup>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" sx={{ mb: 1 }}>Categories</Typography>
                <FormGroup>
                    {categories.map((category) => (
                        <FormControlLabel
                            key={category}
                            control={
                                <Checkbox
                                    checked={selectedCategories.includes(category)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedCategories([...selectedCategories, category]);
                                        } else {
                                            setSelectedCategories(selectedCategories.filter(c => c !== category));
                                        }
                                    }}
                                />
                            }
                            label={category}
                        />
                    ))}
                </FormGroup>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" sx={{ mb: 1 }}>Locations</Typography>
                <FormGroup>
                    {locations.map((location) => (
                        <FormControlLabel
                            key={location}
                            control={
                                <Checkbox
                                    checked={selectedLocations.includes(location)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedLocations([...selectedLocations, location]);
                                        } else {
                                            setSelectedLocations(selectedLocations.filter(l => l !== location));
                                        }
                                    }}
                                />
                            }
                            label={location}
                        />
                    ))}
                </FormGroup>
            </Box>
        </Drawer>
    );
};




const JobDialog = ({ job, open, onClose }) => {
    if (!job) return null;

    const companyConfig = COMPANY_CONFIGS[job.company] || {};

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
            PaperProps={{
                sx: { minHeight: '80vh' }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider',
                pb: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                        src={companyConfig.logo}
                        alt={job.company}
                        sx={{
                            width: 48,
                            height: 48,
                            backgroundColor: companyConfig.color || '#fff',
                            padding: '4px'
                        }}
                    />
                    <Box>
                        <Typography variant="h6" component="div">
                            {job.title}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {job.company}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    {/* Left column - Details */}
                    <Grid item xs={12} md={8}>
                        {/* Job Description */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom color="primary">
                                Description
                            </Typography>
                            <div
                                dangerouslySetInnerHTML={{ __html: job.description }}
                                style={{
                                    '& ul': { paddingLeft: '20px' },
                                    '& p': { marginBottom: '1em' }
                                }}
                            />
                        </Box>

                        {/* Basic Qualifications */}
                        {job.basic_qualifications && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" gutterBottom color="primary">
                                    Basic Qualifications
                                </Typography>
                                <div dangerouslySetInnerHTML={{ __html: job.basic_qualifications }} />
                            </Box>
                        )}

                        {/* Preferred Qualifications */}
                        {job.preferred_qualifications && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" gutterBottom color="primary">
                                    Preferred Qualifications
                                </Typography>
                                <div dangerouslySetInnerHTML={{ __html: job.preferred_qualifications }} />
                            </Box>
                        )}
                    </Grid>

                    {/* Right column - Quick Info */}
                    <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            {/* Location */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Location
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {job.locations.map((location, index) => (
                                        <Chip
                                            key={index}
                                            label={location}
                                            variant="outlined"
                                            size="small"
                                        />
                                    ))}
                                </Box>
                            </Box>

                            {/* Categories */}
                            {job.categories && job.categories.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Categories
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {job.categories.map((category, index) => (
                                            <Chip
                                                key={index}
                                                label={category}
                                                color="primary"
                                                variant="outlined"
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {/* Experience Required */}
                            {job.min_years_of_experience && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Experience Required
                                    </Typography>
                                    <Typography variant="body2">
                                        {job.min_years_of_experience}
                                    </Typography>
                                </Box>
                            )}

                            {/* Date Posted */}
                            {job.earliest_date && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Date Posted
                                    </Typography>
                                    <Typography variant="body2">
                                        {new Date(job.earliest_date).toLocaleDateString()}
                                    </Typography>
                                </Box>
                            )}

                            {/* Apply Button */}
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                href={job.company_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ mt: 2 }}
                            >
                                Apply Now
                            </Button>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

const FAANGWatch = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    useEffect(() => {
        const fetchJobs = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'FAANG_WATCH'));
                let allJobs = [];

                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    const dates = Object.keys(data).filter(key => key.startsWith('jobs_'));
                    const latestDate = dates.sort().reverse()[0];
                    if (data[latestDate]?.batch) {
                        allJobs = [...allJobs, ...data[latestDate].batch];
                    }
                });

                setJobs(allJobs);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching jobs:", error);
                setLoading(false);
            }
        };

        fetchJobs();
    }, []);

    const companies = [...new Set(jobs.map(job => job.company))];
    const categories = [...new Set(jobs.flatMap(job => job.categories || []))];
    const locations = [...new Set(jobs.flatMap(job => job.locations))];

    const filteredJobs = jobs.filter(job => {
        const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            job.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCompany = selectedCompanies.length === 0 || selectedCompanies.includes(job.company);
        const matchesCategory = selectedCategories.length === 0 ||
            (job.categories && job.categories.some(cat => selectedCategories.includes(cat)));
        const matchesLocation = selectedLocations.length === 0 ||
            job.locations.some(loc => selectedLocations.includes(loc));

        return matchesSearch && matchesCompany && matchesCategory && matchesLocation;
    });

    const handleJobClick = (job) => {
        setSelectedJob(job);
        setDialogOpen(true);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
            <AppBar position="static" color="default" elevation={0}>
                <Toolbar>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                        <WorkIcon sx={{ mr: 1 }} /> FAANG Watch
                    </Typography>
                    <IconButton onClick={() => setFilterDrawerOpen(true)}>
                        <TuneIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Paper sx={{ p: 2, mb: 4 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                placeholder="Search jobs by title or description..."
                                variant="outlined"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {selectedCompanies.map(company => (
                                    <Chip
                                        key={company}
                                        label={company}
                                        onDelete={() => setSelectedCompanies(selectedCompanies.filter(c => c !== company))}
                                        avatar={<Avatar src={COMPANY_CONFIGS[company]?.logo} />}
                                    />
                                ))}
                                {selectedCategories.map(category => (
                                    <Chip
                                        key={category}
                                        label={category}
                                        onDelete={() => setSelectedCategories(selectedCategories.filter(c => c !== category))}
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                                {selectedLocations.map(location => (
                                    <Chip
                                        key={location}
                                        label={location}
                                        onDelete={() => setSelectedLocations(selectedLocations.filter(l => l !== location))}
                                    />
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5">
                        {filteredJobs.length} Jobs Found
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {filteredJobs.map((job) => (
                        <Grid item xs={12} sm={6} md={4} key={job.job_id}>
                            <JobCard job={job} onClick={handleJobClick} />
                        </Grid>
                    ))}
                </Grid>

                <JobDialog
                    job={selectedJob}
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                />

                <FilterDrawer
                    open={filterDrawerOpen}
                    onClose={() => setFilterDrawerOpen(false)}
                    companies={companies}
                    selectedCompanies={selectedCompanies}
                    setSelectedCompanies={setSelectedCompanies}
                    categories={categories}
                    selectedCategories={selectedCategories}
                    setSelectedCategories={setSelectedCategories}
                    locations={locations}
                    selectedLocations={selectedLocations}
                    setSelectedLocations={setSelectedLocations}
                />
            </Container>
        </Box>
    );
};

export default FAANGWatch;